import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI, scenariosAPI, analyzerAPI, templatesAPI } from '../lib/api';
import { useProject } from '../lib/project-context';
import { useAuth } from '../lib/auth-context';
import { useProject as useProjectQuery, useScenarios, useReports, useAnalysisRuns, useCreateScenario, useDeleteScenario, useDuplicateScenario, useUpdateProject } from '../lib/queries';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import {
  Plus, Trash2, Copy, Settings2, Play, MapPin, Building2, Calendar,
  Activity, Save, Loader2, Edit, FolderKanban, LineChart, FileBarChart,
  Bot, BarChart3, ArrowRight, CheckCircle2, AlertTriangle, Clock,
  TrendingUp, Users, Layers, RefreshCw, BookmarkPlus, Bookmark
} from 'lucide-react';
import { STATUS_MAP, formatCurrency, ROLE_MAP } from '../lib/utils';
import { bestReturnMetric } from '../lib/returnMetric';
import { useConfirm } from '../components/ui/confirm-dialog';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const STATE_OPTIONS = [['draft','Borrador'],['in_analysis','En análisis'],['active','Activo'],['en_planeacion','En planeación'],['en_preventa','En preventa'],['en_construccion','En construcción'],['en_ventas','En ventas'],['completado','Completado']];
const TYPE_LABELS = { residencial:'Residencial',comercial:'Comercial',industrial:'Industrial',mixto:'Mixto',equipamiento:'Equipamiento',hotelero:'Hotelero',oficinas:'Oficinas' };
const SEGMENT_LABELS = { interes_social:'Interés Social',economico:'Económico',medio:'Medio',residencial:'Residencial',residencial_plus:'Residencial Plus',premium:'Premium' };

const WORKSPACE_TABS = [
  { key: 'overview', label: 'Overview', icon: FolderKanban },
  { key: 'scenarios', label: 'Escenarios', icon: Layers },
  { key: 'analyzer', label: 'Financial Intelligence', icon: LineChart },
  { key: 'reports', label: 'Reportes', icon: FileBarChart },
  { key: 'settings', label: 'Configuración', icon: Settings2 },
];

/* ════════════════════════════════════════════
   Scenario Card
   ════════════════════════════════════════════ */
function ScenarioCard({ scenario, projectId, isActive, onSelect, onDuplicate, onDelete, onAnalyze, onWizard, onRename }) {
  const a = scenario.assumptions || {};
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(scenario.name);

  const handleSaveName = () => {
    setEditing(false);
    if (name.trim() && name !== scenario.name) {
      onRename(scenario, name.trim());
    } else {
      setName(scenario.name);
    }
  };

  return (
    <Card
      className={`rounded-xl border-border/50 shadow-sm transition-all duration-200 cursor-pointer hover:shadow-md ${isActive ? 'ring-2' : ''}`}
      style={isActive ? { borderColor: 'var(--primary-hex)', ringColor: 'rgba(15,157,170,0.25)' } : {}}
      onClick={() => onSelect(scenario)}
      data-testid={`scenario-card-${scenario.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              {editing ? (
                <input
                  className="font-display font-medium text-sm px-1.5 py-0.5 rounded-md border border-border bg-background w-full"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  onBlur={handleSaveName}
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') { setEditing(false); setName(scenario.name); } }}
                  onClick={e => e.stopPropagation()}
                  autoFocus
                  data-testid="scenario-rename-input"
                />
              ) : (
                <h3 className="font-display font-medium text-sm truncate cursor-text" onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }} title="Doble clic para renombrar">
                  {scenario.name}
                </h3>
              )}
              {isActive && (
                <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full text-white" style={{ background: 'var(--primary-hex)' }}>
                  Activo
                </span>
              )}
              {scenario.is_locked && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600">Bloqueado</Badge>}
              {scenario.is_outdated && (
                <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                  style={{ background: 'var(--warning-soft)', color: 'var(--warning-color)', border: '1px solid rgba(245,166,35,0.2)' }}
                  data-testid={`outdated-badge-${scenario.id}`}>
                  Desactualizado
                </span>
              )}
            </div>
            {scenario.description && <p className="text-[11px] text-muted-foreground truncate">{scenario.description}</p>}
          </div>
          <Badge variant="secondary" className="text-[10px] shrink-0" style={scenario.is_outdated ? { background: 'var(--warning-soft)', color: 'var(--warning-color)' } : {}}>
            motor v{scenario.last_engine_version || '?'}
          </Badge>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div className="text-center p-2 rounded-lg bg-muted/30">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Unidades</p>
            <p className="text-sm font-display font-semibold tabular-nums">{a.total_units || 0}</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-muted/30">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Precio/ud</p>
            <p className="text-sm font-display font-semibold tabular-nums">{a.avg_price_per_unit ? `$${(a.avg_price_per_unit / 1_000_000).toFixed(1)}M` : '--'}</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-muted/30">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Obra</p>
            <p className="text-sm font-display font-semibold tabular-nums">{a.construction_months || 0}m</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
          <Button size="sm" className="flex-1 h-8 text-xs text-white hover:opacity-90" style={{ background: 'var(--primary-hex)' }}
            onClick={() => onAnalyze(scenario)} data-testid={`scenario-analyze-${scenario.id}`}>
            <Play className="h-3 w-3 mr-1" /> Analizar
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => onWizard(scenario)} data-testid={`scenario-wizard-${scenario.id}`}>
            <Settings2 className="h-3 w-3 mr-1" /> Inputs
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onDuplicate(scenario)} title="Duplicar"><Copy className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => onDelete(scenario)} disabled={scenario.is_locked} title="Eliminar"><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ════════════════════════════════════════════
   Workspace Tab Button
   ════════════════════════════════════════════ */
function WorkspaceTab({ tab, isActive, onClick, count }) {
  const Icon = tab.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`workspace-tab-${tab.key}`}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all duration-150 ${
        isActive
          ? 'border-current text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
      }`}
      style={isActive ? { color: 'var(--primary-hex)' } : {}}
    >
      <Icon className="h-4 w-4" />
      {tab.label}
      {count !== undefined && <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded-full tabular-nums">{count}</span>}
    </button>
  );
}

/* ════════════════════════════════════════════
   MAIN WORKSPACE
   ════════════════════════════════════════════ */
function StatusBadge({ status, projectId }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(status);
  const updateProjectMut = useUpdateProject();
  const stateInfo = STATUS_MAP[current] || { label: current, color: 'bg-gray-100 text-gray-700' };

  const handleChange = async (newStatus) => {
    setOpen(false);
    setCurrent(newStatus);
    try {
      await updateProjectMut.mutateAsync({ id: projectId, data: { status: newStatus } });
      toast.success(`Estado: ${STATUS_MAP[newStatus]?.label || newStatus}`);
    } catch { toast.error('Error'); setCurrent(status); }
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} data-testid="status-badge-btn">
        <Badge variant="secondary" className={`${stateInfo.color} cursor-pointer hover:opacity-80`}>{stateInfo.label}</Badge>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 z-50 min-w-[180px] rounded-lg shadow-lg border border-border bg-card py-1" data-testid="status-dropdown">
            {Object.entries(STATUS_MAP).map(([key, info]) => (
              <button key={key} onClick={() => handleChange(key)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-muted transition-colors">
                <Badge variant="secondary" className={`${info.color} text-[10px]`}>{info.label}</Badge>
                {key === current && <CheckCircle2 className="h-3 w-3 ml-auto" style={{ color: 'var(--success)' }} />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setActiveProject, setActiveScenario, activeScenario, refreshProjects } = useProject();

  // React Query
  const { data: project, isLoading: projectLoading } = useProjectQuery(projectId);
  const { data: scenarios = [] } = useScenarios(projectId);
  const { data: reports = [] } = useReports(projectId);
  const { data: analysisRuns = [] } = useAnalysisRuns(projectId);
  const createScenarioMut = useCreateScenario();
  const deleteScenarioMut = useDeleteScenario();
  const duplicateScenarioMut = useDuplicateScenario();
  const updateProjectMut = useUpdateProject();

  const [activity, setActivity] = useState([]);
  const loading = projectLoading && !project;
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [newScenario, setNewScenario] = useState({ name: '', description: '' });
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [runningAnalysis, setRunningAnalysis] = useState(null);
  const confirm = useConfirm();

  // Set active project + load activity
  if (project && !editData.id) {
    setEditData(project);
    setActiveProject(project);
  }
  useState(() => {
    projectsAPI.activity(projectId).then(r => setActivity(r.data)).catch(() => {});
  });

  // ── Handlers ──
  const handleSaveProject = async () => {
    setSaving(true);
    try {
      await updateProjectMut.mutateAsync({ id: projectId, data: { name: editData.name, description: editData.description, location: editData.location, project_type: editData.project_type, status: editData.status } });
      setEditing(false);
      refreshProjects();
      toast.success('Proyecto actualizado');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const handleCreateScenario = async () => {
    if (!newScenario.name.trim()) return;
    try {
      const data = await createScenarioMut.mutateAsync({ projectId, data: newScenario });
      toast.success(`Escenario "${data.name}" creado`);
      setShowCreate(false);
      setNewScenario({ name: '', description: '' });
      setSelectedTemplateId('');
      setActiveScenario(data);
      navigate(`/analyzer/wizard/${projectId}/${data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplateId) { toast.error('Selecciona una plantilla'); return; }
    const name = newScenario.name.trim() || 'Escenario desde plantilla';
    setApplyingTemplate(true);
    try {
      const { data } = await templatesAPI.apply(selectedTemplateId, {
        project_id: projectId,
        scenario_name: name,
      });
      toast.success(`Plantilla aplicada — ${data.assumptions_applied} inputs, ${data.typologies_applied} tipologías`);
      setShowCreate(false);
      setNewScenario({ name: '', description: '' });
      setSelectedTemplateId('');
      // Redirigir al wizard del nuevo escenario
      navigate(`/analyzer/wizard/${projectId}/${data.scenario_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo aplicar la plantilla'); }
    setApplyingTemplate(false);
  };

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const { data } = await templatesAPI.list();
      setAvailableTemplates(data || []);
    } catch { /* silencioso */ }
    setLoadingTemplates(false);
  };

  const handleDeleteScenario = async (s) => {
    const ok = await confirm({
      title: `¿Eliminar escenario "${s.name}"?`,
      description: 'El escenario se removerá del proyecto. Los runs históricos se preservan en el historial pero ya no contarán en las métricas del proyecto.',
      confirmLabel: 'Eliminar',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await deleteScenarioMut.mutateAsync({ projectId, scenarioId: s.id });
      toast.success('Eliminado');
    } catch (err) { console.error('[ProjectDetail] delete scenario failed', err); toast.error('Error'); }
  };

  const handleDuplicate = async (s) => {
    try {
      await duplicateScenarioMut.mutateAsync({ projectId, scenarioId: s.id });
      toast.success('Duplicado');
    } catch (err) { console.error('[ProjectDetail] duplicate failed', err); toast.error('Error'); }
  };

  const handleSelectScenario = (s) => {
    setActiveScenario(s);
    toast.success(`Escenario activo: ${s.name}`);
  };

  // L-85 · «Analizar» es navegación, no registro. Llamaba a /analyzer/run —el
  // único llamador que PERSISTE— y del resultado sólo leía `duration_ms` para un
  // toast. El Analyzer previsualiza al montarse (Analyzer.jsx:301), así que la
  // llamada dejaba una corrida registrada que el usuario no pidió y computaba
  // dos veces. Registrar sigue siendo del botón explícito de SWA-244.
  const handleAnalyze = (s) => {
    setActiveScenario(s);
    navigate(`/analyzer?project=${projectId}&scenario=${s.id}`);
  };

  const handleRenameScenario = async (s, newName) => {
    try {
      await scenariosAPI.update(projectId, s.id, { name: newName });
      // Invalidate scenarios cache to refresh the list
      const { queryClient } = await import('../lib/query-client');
      queryClient.invalidateQueries({ queryKey: ['scenarios', projectId] });
      toast.success(`Renombrado a "${newName}"`);
    } catch { toast.error('Error al renombrar'); }
  };

  const handleWizard = (s) => {
    setActiveScenario(s);
    navigate(`/analyzer/wizard/${projectId}/${s.id}`);
  };

  // ── Loading / Not Found ──
  if (loading) {
    return (
      <div className="px-6 lg:px-8 py-6" data-testid="workspace-loading">
        <Skeleton className="h-8 w-80 mb-2" />
        <Skeleton className="h-4 w-48 mb-6" />
        <Skeleton className="h-10 w-full mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <FolderKanban className="h-12 w-12 mx-auto mb-3" style={{ color: 'var(--primary-hex)', opacity: 0.3 }} />
          <p className="text-muted-foreground mb-3">Proyecto no encontrado</p>
          <Button variant="outline" onClick={() => navigate('/projects')}>Volver a proyectos</Button>
        </div>
      </div>
    );
  }

  const stateInfo = STATUS_MAP[project.status] || { label: project.status, color: 'bg-gray-100 text-gray-700' };

  // Project metrics: from the first scenario (they share the same base project data)
  const baseScenario = scenarios[0]?.assumptions || {};
  const projectUnits = baseScenario.total_units || 0;
  const projectRevenue = projectUnits * (baseScenario.avg_price_per_unit || 0);

  // Comparative metrics: best TIR and VAN across scenarios with completed analyses
  // Solo escenarios ACTIVOS (no soft-deleted). Runs históricos de escenarios ya
  // borrados quedan en DB por diseño (piso operativo preserva historial), pero NO
  // deben inflar métricas del proyecto vivo.
  const activeScenarioIds = new Set(scenarios.map(s => s.id));
  const scenarioMetrics = analysisRuns
    .filter(run => activeScenarioIds.has(run.scenario_id))
    .reduce((acc, run) => {
      const m = run.metrics || {};
      const sid = run.scenario_id;
      if (!acc[sid] || (run.created_at > acc[sid].created_at)) {
        acc[sid] = { ...m, created_at: run.created_at, scenario_id: sid };
      }
      return acc;
    }, {});

  const validVANs = Object.values(scenarioMetrics).filter(m => m.npv != null);
  const bestVAN = validVANs.length > 0 ? Math.max(...validVANs.map(m => m.npv)) : null;
  // SWA-134 · el resumen publica la métrica del INVERSIONISTA (la misma que la lista,
  // getReturnMetric), no irr_unlevered. Solo se comparan métricas iguales:
  // TIR confiable > MIRR > MOIC.
  const bestRM = bestReturnMetric(Object.values(scenarioMetrics));

  return (
    <div className="h-full flex flex-col" data-testid="project-workspace">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }} className="flex-1 flex flex-col overflow-hidden">

        {/* ── Workspace Header ── */}
        <div className="px-6 lg:px-8 pt-5 pb-0 shrink-0">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="font-display text-2xl font-semibold tracking-tight" data-testid="workspace-title">{project.name}</h1>
                <StatusBadge status={project.status} projectId={projectId} />
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                {project.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {project.location}</span>}
                <span className="flex items-center gap-1" title={`Tipo: ${TYPE_LABELS[project.project_type] || project.project_type || '—'}`}>
                  <Building2 className="h-3 w-3" /> {TYPE_LABELS[project.project_type] || project.project_type || 'Sin tipo'}
                </span>
                <span className="flex items-center gap-1"><Layers className="h-3 w-3" /> {scenarios.length} escenarios</span>
                {project.updated_at && (
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(project.updated_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {scenarios.some(s => s.is_outdated) && (
                <Button size="sm" variant="outline" className="text-xs gap-1.5"
                  style={{ borderColor: 'var(--warning-color)', color: 'var(--warning-color)' }}
                  onClick={async () => {
                    toast.info('Recalculando todos los escenarios...');
                    try {
                      await analyzerAPI.recalculateAll({ scenario_id: 'all', project_id: projectId });
                      const { queryClient } = await import('../lib/query-client');
                      queryClient.invalidateQueries({ queryKey: ['scenarios', projectId] });
                      toast.success('Todos los escenarios recalculados');
                    } catch { toast.error('Error al recalcular'); }
                  }}
                  data-testid="recalculate-all-btn">
                  <RefreshCw className="h-3.5 w-3.5" /> Recalcular todos
                </Button>
              )}
              <Dialog open={showCreate} onOpenChange={(v) => { setShowCreate(v); if (v) loadTemplates(); }}>
                <DialogTrigger asChild>
                  <Button size="sm" style={{ background: 'var(--accent-primary)' }} className="text-white hover:opacity-90" data-testid="create-scenario-btn">
                    <Plus className="mr-1.5 h-3.5 w-3.5" /> Nuevo escenario
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md" aria-describedby="create-scenario-desc">
                  <DialogHeader><DialogTitle className="font-display">Nuevo escenario</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2"><Label>Nombre</Label><Input value={newScenario.name} onChange={(e) => setNewScenario({...newScenario, name: e.target.value})} placeholder="Caso Base, Optimista, Pesimista" data-testid="scenario-name-input" autoFocus /></div>
                    <div className="space-y-2"><Label>Descripción (opcional)</Label><Input value={newScenario.description} onChange={(e) => setNewScenario({...newScenario, description: e.target.value})} placeholder="Breve descripción" /></div>

                    {/* Sprint 6 · Semana 3.2 — Aplicar plantilla */}
                    {availableTemplates.length > 0 && (
                      <div className="space-y-2 pt-2 border-t border-border/40">
                        <Label className="flex items-center gap-1.5 text-xs">
                          <Bookmark className="h-3.5 w-3.5" style={{ color: '#6366F1' }} />
                          Partir desde plantilla (opcional)
                        </Label>
                        <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                          <SelectTrigger data-testid="template-select-trigger">
                            <SelectValue placeholder={loadingTemplates ? 'Cargando plantillas...' : 'Escenario vacío (default)'} />
                          </SelectTrigger>
                          <SelectContent>
                            {availableTemplates.map(t => (
                              <SelectItem key={t.id} value={t.id} data-testid={`template-option-${t.id}`}>
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium">{t.name}</span>
                                  <span className="text-[10px] text-muted-foreground">
                                    {t.assumption_count} inputs · {t.typology_count} tipologías
                                    {t.usage_count > 0 && ` · ${t.usage_count} usos`}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectedTemplateId && (
                          <p className="text-[10px] text-muted-foreground italic">
                            El escenario se creará con todos los inputs, tipologías, cobranza y waterfall de la plantilla.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => { setShowCreate(false); setSelectedTemplateId(''); }}>Cancelar</Button>
                    {selectedTemplateId ? (
                      <Button
                        onClick={handleApplyTemplate}
                        disabled={applyingTemplate || !newScenario.name.trim()}
                        data-testid="scenario-from-template-button"
                        style={{ background: '#6366F1' }}
                        className="text-white"
                      >
                        {applyingTemplate ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Bookmark className="h-4 w-4 mr-1.5" />}
                        Crear desde plantilla
                      </Button>
                    ) : (
                      <Button onClick={handleCreateScenario} disabled={!newScenario.name.trim()} data-testid="scenario-save-button" style={{ background: 'var(--accent-primary)' }} className="text-white">Crear y configurar</Button>
                    )}
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* ── Tab bar ── */}
          <div className="flex items-center border-b border-border/50 -mx-6 lg:-mx-8 px-6 lg:px-8 gap-0" data-testid="workspace-tabs">
            {WORKSPACE_TABS.map(tab => (
              <WorkspaceTab
                key={tab.key}
                tab={tab}
                isActive={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
                count={tab.key === 'scenarios' ? scenarios.length : tab.key === 'reports' ? reports.length : undefined}
              />
            ))}
          </div>
        </div>

        {/* ── Tab Content ── */}
        <div className="flex-1 overflow-y-auto px-6 lg:px-8 py-5">

          {/* ════ OVERVIEW ════ */}
          {activeTab === 'overview' && (
            <div className="space-y-6 max-w-[1400px]">
              {/* KPI Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="workspace-kpis">
                {[
                  { label: 'Unidades', value: projectUnits || '--', icon: Building2, color: 'var(--success)' },
                  { label: 'Ingreso estimado', value: projectRevenue > 0 ? formatCurrency(projectRevenue) : '--', icon: TrendingUp, color: 'var(--warning-color)' },
                  // SWA-139 · el normalizador es COMPUERTA, no sustituto: la magnitud sigue siendo irr_unlevered hasta que SWA-160 decida.
                  { label: `Mejor ${bestRM ? bestRM.label : 'retorno'} del inversionista`, value: bestRM ? bestRM.display : '--', icon: LineChart, color: '#6366f1', note: (bestRM && !bestRM.reliable) ? bestRM.note : null, testid: 'kpi-best-return' },
                  { label: 'Mejor VAN del inversionista', value: bestVAN != null ? formatCurrency(bestVAN) : '--', icon: BarChart3, color: 'var(--success)' },
                ].map((kpi, i) => (
                  <Card key={i} className="rounded-xl border-border/50 shadow-sm" data-testid={kpi.testid}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{kpi.label}</p>
                          <p className="text-xl font-display font-semibold mt-1 tabular-nums">{kpi.value}</p>
                          {kpi.note && <p className="text-[9px] mt-0.5 leading-tight" style={{ color: 'var(--warning-color)' }} data-testid="kpi-return-note">{kpi.note}</p>}
                        </div>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${kpi.color}12` }}>
                          <kpi.icon className="h-4 w-4" style={{ color: kpi.color }} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Scenarios Preview */}
                <div className="lg:col-span-2 space-y-3">
                  <div className="flex items-center justify-between">
                    <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">Escenarios</h2>
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setActiveTab('scenarios')}>Ver todos <ArrowRight className="ml-1 h-3 w-3" /></Button>
                  </div>
                  {scenarios.length === 0 ? (
                    <Card className="rounded-xl border-border/50 border-dashed">
                      <CardContent className="py-10 text-center">
                        <Layers className="h-8 w-8 mx-auto mb-3 text-muted-foreground/30" />
                        <p className="text-sm text-muted-foreground mb-3">Sin escenarios. Crea tu primero para empezar a analizar.</p>
                        <Button size="sm" onClick={() => setShowCreate(true)} style={{ background: 'var(--accent-primary)' }} className="text-white hover:opacity-90">
                          <Plus className="mr-1.5 h-3.5 w-3.5" /> Crear escenario
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {scenarios.slice(0, 4).map(s => (
                        <ScenarioCard
                          key={s.id}
                          scenario={s}
                          projectId={projectId}
                          isActive={activeScenario?.id === s.id}
                          onSelect={handleSelectScenario}
                          onDuplicate={handleDuplicate}
                          onDelete={handleDeleteScenario}
                          onAnalyze={handleAnalyze}
                          onWizard={handleWizard}
                          onRename={handleRenameScenario}
                        />
                      ))}
                    </div>
                  )}
                  {scenarios.length > 4 && (
                    <Button variant="outline" className="w-full text-xs" onClick={() => setActiveTab('scenarios')}>
                      Ver los {scenarios.length} escenarios
                    </Button>
                  )}
                </div>

                {/* Right Column: Quick Actions + Activity */}
                <div className="space-y-5">
                  {/* Quick Actions */}
                  <div>
                    <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Acciones rápidas</h2>
                    <div className="space-y-2">
                      <Button variant="outline" className="w-full justify-start h-10 text-sm" onClick={() => setShowCreate(true)} data-testid="quick-create-scenario">
                        <Plus className="mr-2 h-4 w-4" style={{ color: 'var(--primary-hex)' }} /> Nuevo escenario
                      </Button>
                      <Button variant="outline" className="w-full justify-start h-10 text-sm" onClick={() => { setActiveTab('analyzer'); }} data-testid="quick-analyzer">
                        <LineChart className="mr-2 h-4 w-4" style={{ color: 'var(--primary-hex)' }} /> Financial Intelligence
                      </Button>
                      <Button variant="outline" className="w-full justify-start h-10 text-sm" onClick={() => navigate('/reports')} data-testid="quick-reports">
                        <FileBarChart className="mr-2 h-4 w-4" style={{ color: 'var(--primary-hex)' }} /> Generar reporte
                      </Button>
                      <Button variant="outline" className="w-full justify-start h-10 text-sm" onClick={() => navigate('/copilot')} data-testid="quick-copilot">
                        <Bot className="mr-2 h-4 w-4" style={{ color: 'var(--primary-hex)' }} /> Consultar Copilot
                      </Button>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div>
                    <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Actividad reciente</h2>
                    <Card className="rounded-xl border-border/50">
                      <CardContent className="p-3">
                        {activity.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-4">Sin actividad registrada</p>
                        ) : (
                          <div className="space-y-2.5">
                            {activity.slice(0, 6).map((log, i) => (
                              <div key={log.id || i} className="flex items-start gap-2.5">
                                <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: log.action === 'delete' ? 'var(--danger)' : log.action === 'create' ? 'var(--success)' : 'var(--primary-hex)' }} />
                                <div className="min-w-0">
                                  <p className="text-xs truncate"><span className="font-medium">{{ create: 'Creó', update: 'Actualizó', delete: 'Eliminó', run_analysis: 'Ejecutó análisis', duplicate: 'Duplicó', generate_report: 'Generó reporte', archive: 'Archivó', restore: 'Restauró', 'update scenario': 'Actualizó escenario', 'create scenario': 'Creó escenario', 'update assumptions': 'Actualizó supuestos' }[log.action] || log.action}</span> <span className="text-muted-foreground">{{ project: 'proyecto', scenario: 'escenario', report: 'reporte', analysis_run: 'análisis', analysis: 'análisis', user: 'usuario', company: 'empresa' }[log.entity_type] || log.entity_type}</span></p>
                                  <p className="text-[10px] text-muted-foreground tabular-nums">{log.created_at ? new Date(log.created_at).toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ════ SCENARIOS ════ */}
          {activeTab === 'scenarios' && (
            <div className="max-w-[1200px] space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{scenarios.length} escenarios en este proyecto</p>
              </div>
              {scenarios.length === 0 ? (
                <Card className="rounded-xl border-border/50 border-dashed">
                  <CardContent className="py-16 text-center">
                    <Layers className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
                    <h3 className="font-display font-medium mb-2">Sin escenarios</h3>
                    <p className="text-sm text-muted-foreground mb-4">Crea tu primer escenario financiero para empezar a modelar.</p>
                    <Button size="sm" onClick={() => setShowCreate(true)} style={{ background: 'var(--accent-primary)' }} className="text-white hover:opacity-90">
                      <Plus className="mr-2 h-4 w-4" /> Crear escenario
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="scenario-grid">
                  {scenarios.map(s => (
                    <ScenarioCard
                      key={s.id}
                      scenario={s}
                      projectId={projectId}
                      isActive={activeScenario?.id === s.id}
                      onSelect={handleSelectScenario}
                      onDuplicate={handleDuplicate}
                      onDelete={handleDeleteScenario}
                      onAnalyze={handleAnalyze}
                      onWizard={handleWizard}
                      onRename={handleRenameScenario}
                    />
                  ))}
                  {/* Create new card */}
                  <Card
                    className="rounded-xl border-border/50 border-dashed cursor-pointer hover:bg-accent/30 transition-colors duration-150"
                    onClick={() => setShowCreate(true)}
                    data-testid="create-scenario-card"
                  >
                    <CardContent className="flex flex-col items-center justify-center p-6 min-h-[200px]">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: 'rgba(15,157,170,0.08)' }}>
                        <Plus className="h-5 w-5" style={{ color: 'var(--primary-hex)' }} />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">Nuevo escenario</p>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}

          {/* ════ FINANCIAL ANALYZER ════ */}
          {activeTab === 'analyzer' && (
            <div className="max-w-[1200px] space-y-5">
              <div className="mb-4">
                <p className="text-sm text-muted-foreground">Selecciona un escenario para ejecutar el análisis financiero completo.</p>
              </div>
              {scenarios.length === 0 ? (
                <Card className="rounded-xl border-border/50 border-dashed">
                  <CardContent className="py-16 text-center">
                    <LineChart className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
                    <h3 className="font-display font-medium mb-2">Crea un escenario primero</h3>
                    <p className="text-sm text-muted-foreground mb-4">Necesitas al menos un escenario para ejecutar análisis.</p>
                    <Button size="sm" onClick={() => setShowCreate(true)} style={{ background: 'var(--accent-primary)' }} className="text-white hover:opacity-90">
                      <Plus className="mr-2 h-4 w-4" /> Crear escenario
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {scenarios.map(s => {
                    const a = s.assumptions || {};
                    const isRunning = runningAnalysis === s.id;
                    return (
                      <Card key={s.id} className="rounded-xl border-border/50 shadow-sm hover:shadow-md transition-all duration-200">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <h3 className="font-display font-medium text-sm">{s.name}</h3>
                                {activeScenario?.id === s.id && (
                                  <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full text-white" style={{ background: 'var(--primary-hex)' }}>Activo</span>
                                )}
                              </div>
                              <p className="text-[11px] text-muted-foreground tabular-nums">
                                {a.total_units || 0} uds | {a.avg_price_per_unit ? formatCurrency(a.avg_price_per_unit) : '--'}/ud | {a.construction_months || 0}m obra
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => handleWizard(s)}>
                                <Settings2 className="h-3 w-3 mr-1" /> Editar inputs
                              </Button>
                              <Button size="sm" className="h-8 text-xs text-white hover:opacity-90" style={{ background: 'var(--primary-hex)' }}
                                onClick={() => handleAnalyze(s)} disabled={isRunning}>
                                {isRunning ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Play className="h-3 w-3 mr-1" />}
                                {isRunning ? 'Analizando...' : 'Ejecutar análisis'}
                              </Button>
                              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setActiveScenario(s); navigate('/analyzer'); }}>
                                Abrir completo <ArrowRight className="ml-1 h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Recent analysis runs */}
              {analysisRuns.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Últimas ejecuciones</h3>
                  <Card className="rounded-xl border-border/50">
                    <CardContent className="p-3">
                      <div className="space-y-2">
                        {analysisRuns.slice(0, 5).map((run, i) => {
                          const scenario = scenarios.find(s => s.id === run.scenario_id);
                          return (
                            <div key={run.id || i} className="flex items-center gap-3 text-xs py-1.5">
                              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" style={{ color: run.status === 'completed' ? 'var(--success)' : 'var(--danger)' }} />
                              <span className="font-medium truncate flex-1">{scenario?.name || run.scenario_id}</span>
                              <span className="text-muted-foreground tabular-nums">{run.duration_ms}ms</span>
                              <span className="text-muted-foreground tabular-nums">
                                {run.created_at ? new Date(run.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}

          {/* ════ REPORTS ════ */}
          {activeTab === 'reports' && (
            <div className="max-w-[1200px] space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{reports.length} reportes generados</p>
                <Button size="sm" variant="outline" onClick={() => navigate('/reports')} data-testid="open-reports-module">
                  <FileBarChart className="mr-1.5 h-3.5 w-3.5" /> Abrir módulo de reportes
                </Button>
              </div>
              {reports.length === 0 ? (
                <Card className="rounded-xl border-border/50 border-dashed">
                  <CardContent className="py-16 text-center">
                    <FileBarChart className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
                    <h3 className="font-display font-medium mb-2">Sin reportes</h3>
                    <p className="text-sm text-muted-foreground mb-4">Genera tu primer reporte desde el módulo de reportes.</p>
                    <Button size="sm" variant="outline" onClick={() => navigate('/reports')}>Ir a Reportes</Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {reports.map(r => (
                    <Card key={r.id} className="rounded-xl border-border/50 shadow-sm hover:shadow-md transition-all duration-200">
                      <CardContent className="p-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(15,157,170,0.08)' }}>
                            <FileBarChart className="h-4 w-4" style={{ color: 'var(--primary-hex)' }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{r.report_label}</p>
                            <p className="text-[11px] text-muted-foreground">{r.scenario_name} | {r.created_at ? new Date(r.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                          </div>
                          <Button variant="outline" size="sm" className="h-7 text-xs shrink-0" onClick={() => navigate('/reports')}>Ver</Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ════ SETTINGS ════ */}
          {activeTab === 'settings' && (
            <div className="max-w-[800px]">
              <Card className="rounded-xl border-border/50 shadow-sm">
                <CardContent className="p-6 space-y-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-display font-semibold">Datos del proyecto</h3>
                    {!editing && (
                      <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                        <Edit className="h-3.5 w-3.5 mr-1.5" /> Editar
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                    <div className="space-y-1.5"><Label className="text-xs">Nombre</Label><Input value={editData.name || ''} onChange={(e) => setEditData({...editData, name: e.target.value})} disabled={!editing} /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Ubicación</Label><Input value={editData.location || ''} onChange={(e) => setEditData({...editData, location: e.target.value})} disabled={!editing} /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Tipo de desarrollo</Label>
                      <Select value={editData.project_type || 'residencial'} onValueChange={(v) => setEditData({...editData, project_type: v})} disabled={!editing}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5"><Label className="text-xs">Estado</Label>
                      <Select value={editData.status || 'draft'} onValueChange={(v) => setEditData({...editData, status: v})} disabled={!editing}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{STATE_OPTIONS.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5 md:col-span-2"><Label className="text-xs">Descripción</Label><Input value={editData.description || ''} onChange={(e) => setEditData({...editData, description: e.target.value})} disabled={!editing} /></div>
                  </div>
                  {editing && (
                    <div className="flex justify-end gap-2 pt-2">
                      <Button variant="outline" onClick={() => { setEditing(false); setEditData(project); }}>Cancelar</Button>
                      <Button onClick={handleSaveProject} disabled={saving} style={{ background: 'var(--accent-primary)' }} className="text-white hover:opacity-90">
                        {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Guardar cambios
                      </Button>
                    </div>
                  )}
                  <Separator />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div><span className="text-muted-foreground">Creado</span><br /><span className="font-medium tabular-nums">{project.created_at ? new Date(project.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' }) : '--'}</span></div>
                    <div><span className="text-muted-foreground">Actualizado</span><br /><span className="font-medium tabular-nums">{project.updated_at ? new Date(project.updated_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' }) : '--'}</span></div>
                    <div><span className="text-muted-foreground">Escenarios</span><br /><span className="font-medium">{scenarios.length}</span></div>
                    <div><span className="text-muted-foreground">Reportes</span><br /><span className="font-medium">{reports.length}</span></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
